/**
 * Morning Brief — 07:30, the front door of the Daily Operating System.
 *
 * It consumes the judged feed GET /me/conversations, ranked by the one Professional Judgment
 * Engine, and surfaces the single most important thing to do first plus the next few. Each item
 * links straight to its underlying matter. The Brief adds only the calm "what changed since you
 * last looked" summary — there is no separate work-queue surface.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Sun, ArrowRight, Bell, CheckCircle, Scales } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { useNotificationsStore, type Notification } from '@/lib/notificationsStore';
import { fetchMyConversations, firstAction, PURPOSE_LABELS } from '@/lib/conversationsApi';

export function MorningBrief() {
  const notifications = useNotificationsStore((s) => s.notifications);

  // The one judged feed — ranked by the Professional Judgment Engine.
  const { data: conversations = [] } = useQuery({
    queryKey: ['my-conversations'],
    queryFn: fetchMyConversations,
    refetchOnWindowFocus: true,
  });

  const first = firstAction(conversations);            // the single most important thing first
  const then = conversations.slice(1, 4);
  const changed = useMemo(() => summariseChanges(notifications), [notifications]);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <Sun className="h-5 w-5 text-amber-500" weight="fill" />
          <h2 className="text-base font-semibold text-foreground">Where to start</h2>
        </div>

        {changed.total > 0 && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span><span className="font-medium text-foreground">{changed.total} update{changed.total !== 1 ? 's' : ''} since you last looked</span>{changed.summary ? ` — ${changed.summary}` : ''}</span>
          </div>
        )}

        {!first ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3 text-sm text-emerald-900 dark:text-emerald-200">
            <CheckCircle className="h-5 w-5" weight="fill" /> You&apos;re on top of everything — nothing needs you first thing. Your day is clear.
          </div>
        ) : (
          <>
            <Link to={first.matterId ? `/barrister/matters/${first.matterId}` : '/barrister'} className="flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 p-3.5 text-brand-900 dark:text-brand-200 hover:bg-brand-100">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">Start here</div>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Scales className="h-4 w-4 shrink-0" /> {PURPOSE_LABELS[first.purpose]}
                  {first.matterNumber && <span className="text-xs font-normal opacity-70">· {first.matterNumber}</span>}
                </div>
                <div className="truncate text-xs opacity-80">{first.preview || first.dispositionLabel}</div>
                {/* WHY — the same reasons the Work Queue shows. */}
                {first.reasons.length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] opacity-80">
                    {first.reasons.slice(0, 3).map((r) => <span key={r.factor} className="rounded bg-white/60 px-1.5 py-0.5">{r.factor}</span>)}
                  </div>
                )}
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white/70 px-3 py-1.5 text-xs font-semibold">
                {first.action} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>

            {then.length > 0 && (
              <div className="mt-3">
                <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Then</div>
                <div className="space-y-1.5">
                  {then.map((c) => (
                    <Link key={c.conversationId} to={c.matterId ? `/barrister/matters/${c.matterId}` : '/barrister'} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 hover:bg-muted/40">
                      <div className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                        <span className="truncate">{PURPOSE_LABELS[c.purpose]}{c.matterNumber ? ` · ${c.matterNumber}` : ''}</span>
                      </div>
                      <span className="shrink-0 text-xs font-medium text-brand-600 dark:text-brand-400">{c.action} →</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Summarise unread notifications by category — "47 messages · 12 hearing updates · 3 new
// matters", never a dumped list. (Purely the "since you last looked" context, not judgment.)
function summariseChanges(notifications: Notification[]): { total: number; summary: string } {
  const unread = notifications.filter((n) => !n.read);
  const bucket = (t: string): string => {
    if (t === 'message_received') return 'message';
    if (t.startsWith('hearing') || t === 'judgment_reserved') return 'hearing update';
    if (t.startsWith('conference')) return 'conference update';
    if (t.startsWith('fee_note') || t.startsWith('payment')) return 'fee update';
    if (t === 'matter_created' || t === 'case_assigned' || t === 'matter_needs_allocation') return 'new matter';
    if (t.startsWith('task')) return 'task';
    if (t.startsWith('brief') || t.startsWith('intake')) return 'instruction';
    return 'other';
  };
  const counts = new Map<string, number>();
  for (const n of unread) counts.set(bucket(n.type), (counts.get(bucket(n.type)) ?? 0) + 1);
  const summary = [...counts.entries()]
    .filter(([k]) => k !== 'other')
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([k, c]) => `${c} ${k}${c !== 1 ? 's' : ''}`)
    .join(' · ');
  return { total: unread.length, summary };
}
